import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  UndoRounded as UndoIcon,
  RedoRounded as RedoIcon,
} from '@mui/icons-material';
import Tooltip from '@src/components/Tooltip';

import actions from '@src/redux/actions';
import useCanUndo from '@src/hooks/useCanUndo';
import useCanRedo from '@src/hooks/useCanRedo';
import useUndoRedoShortcuts from '@src/hooks/useUndoRedoShortcuts';
import { HISTORY_SCOPE } from '@src/constants/history';
import { PROJECT_PRODUCT } from '@src/constants/project';
import { isVideoSource } from '@src/services/project';
import { selectHasSynthesizingBlock } from '@src/redux/project/selectors';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { StyledHistoryButton } from './index.style';

const historyTooltipProps = {
  placement: 'bottom',
  componentsProps: {
    tooltip: {
      sx: {
        marginTop: '8px !important',
      },
    },
  },
};

const UndoRedoControls = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const canUndo = useCanUndo(HISTORY_SCOPE.DUBBING);
  const canRedo = useCanRedo(HISTORY_SCOPE.DUBBING);
  const projectId = useSelector((state) => state.project.projectId);
  // One subscription per field, and a derived boolean instead of the block map:
  // the whole-slice form woke this control on every keystroke, only to recompute
  // the same answer.
  const isPreProcessingSynthesis = useSelector(
    (state) => state.project.isPreProcessingSynthesis,
  );
  const isProjectConvertingDraft = useSelector(
    (state) => state.project.isProjectConvertingDraft,
  );
  const isProjectSynthesizing = useSelector(
    (state) => state.project.isProjectSynthesizing,
  );
  const isTranslating = useSelector((state) => state.project.isTranslating);
  const product = useSelector((state) => state.project.product);
  const source = useSelector((state) => state.project.source);
  const anyBlockSynthesizing = useSelector(selectHasSynthesizingBlock);
  const user = useSelector((state) => state.auth.user);
  const { getFeatureValue } = useFeatureFlags();

  // Same gate as the create-audio flow (flag + DUBBING + video source). Only
  // that flow converts per block via the block's own status; keeping the exact
  // condition ensures the flag OFF leaves every other flow's undo/redo untouched.
  const canCreateBlockAudio =
    getFeatureValue(FEATURE_KEYS.VIDEO_DUBBING_CREATE_AUDIO, {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    }) &&
    product === PROJECT_PRODUCT.DUBBING &&
    isVideoSource(source);

  // Per-block create-audio convert never sets the project-wide synthesis flags —
  // only the block's own status — so guard on that too, else undo/redo would
  // stay enabled mid-convert.
  const hasSynthesizingBlock = canCreateBlockAudio && anyBlockSynthesizing;
  const isHistoryDisabled =
    isTranslating ||
    isPreProcessingSynthesis ||
    isProjectConvertingDraft ||
    isProjectSynthesizing ||
    hasSynthesizingBlock;

  useUndoRedoShortcuts(HISTORY_SCOPE.DUBBING, {
    disabled: isHistoryDisabled,
  });

  useEffect(
    () => () => {
      dispatch(actions.history.clear(HISTORY_SCOPE.DUBBING));
    },
    [projectId, dispatch],
  );

  const handleUndo = () => {
    dispatch(actions.history.undo(HISTORY_SCOPE.DUBBING));
  };

  const handleRedo = () => {
    dispatch(actions.history.redo(HISTORY_SCOPE.DUBBING));
  };

  const preventEditorBlur = (event) => {
    event.preventDefault();
  };

  return (
    <>
      <Tooltip arrow title={t('undo')} {...historyTooltipProps}>
        <span
          style={{
            display: 'inline-flex',
            cursor: canUndo && !isHistoryDisabled ? 'pointer' : 'not-allowed',
          }}
        >
          <StyledHistoryButton
            onMouseDown={preventEditorBlur}
            onClick={handleUndo}
            disabled={!canUndo || isHistoryDisabled}
          >
            <UndoIcon sx={{ fontSize: 24 }} />
          </StyledHistoryButton>
        </span>
      </Tooltip>
      <Tooltip arrow title={t('redo')} {...historyTooltipProps}>
        <span
          style={{
            display: 'inline-flex',
            cursor: canRedo && !isHistoryDisabled ? 'pointer' : 'not-allowed',
          }}
        >
          <StyledHistoryButton
            onMouseDown={preventEditorBlur}
            onClick={handleRedo}
            disabled={!canRedo || isHistoryDisabled}
          >
            <RedoIcon sx={{ fontSize: 24 }} />
          </StyledHistoryButton>
        </span>
      </Tooltip>
    </>
  );
};

export default UndoRedoControls;
